import { useEffect, useState } from "react";
import type { MagicAssets } from "@greg-chick/shared";
import { contentBase } from "./contentApi";

/**
 * 📦 **저장소가 아는 그림표**(`content/asset-manifest.json` — `pnpm content:build` 가 만든다).
 *
 * 층 그림 자리에 「저장소에도 이 그림이 있어요」를 알려 주려고 읽는다(`repoArtOffer`).
 * 한 번만 읽어 둔다 — 책 한 권의 쪽마다 편집기가 뜨는데 매번 받으면 수십 번이다.
 *
 * 🔴 **못 읽었으면 `null`**(빈 표가 아니다). 빈 표를 돌려주면 「저장소엔 그런 그림이 없다」로 읽혀
 *    있는 길을 안 보여 준다 — 「없다」와 「못 물어봤다」는 다른 말이다.
 *    대신 실패한 것을 붙들어 두지 않는다 — 다음에 편집기를 열면 다시 물어본다.
 */
let cached: Promise<MagicAssets | null> | null = null;

export function loadRepoAssets(): Promise<MagicAssets | null> {
  if (!cached) {
    cached = fetch(`${contentBase()}/content/asset-manifest.json`, { cache: "no-store" })
      .then(async (r) => (r.ok ? (((await r.json()) as { assets?: MagicAssets }).assets ?? null) : null))
      .catch(() => null) // 못 물어봤다 — 곁들이는 정보라 편집을 막지 않는다(아래에서 null 을 그대로 쓴다)
      .then((a) => {
        if (!a) cached = null; // 실패는 붙들지 않는다
        return a;
      });
  }
  return cached;
}

/** 검사용 — 모듈에 붙든 것을 비운다. */
export function resetRepoAssets(): void {
  cached = null;
}

export function useRepoAssets(): MagicAssets | null {
  const [a, setA] = useState<MagicAssets | null>(null);
  useEffect(() => {
    let alive = true;
    void loadRepoAssets().then((x) => alive && setA(x));
    return () => {
      alive = false;
    };
  }, []);
  return a;
}

/**
 * ✏️ **원본 책**(저장소 `book.<id>.json` — 스캔본을 옮겨 적은 글). 게시본 자막과 견줄 때 쓴다.
 * 못 읽으면 `null` — 「빠진 말 없음」이 아니라 「못 물어봤다」다.
 */
export async function loadRepoBookDoc(bookId: string): Promise<{ pages: Array<Record<string, unknown>> } | null> {
  try {
    const r = await fetch(`${contentBase()}/content/book.${encodeURIComponent(bookId)}.json`, { cache: "no-store" });
    if (!r.ok) return null;
    const d = (await r.json()) as { pages?: unknown };
    return Array.isArray(d?.pages) ? { pages: d.pages as Array<Record<string, unknown>> } : null;
  } catch {
    return null; // 못 물어봤다 — 부르는 쪽은 카드만 안 띄운다(편집은 막지 않는다)
  }
}
